import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Checkbox } from '@/components/ui/checkbox'
import { PhoneInput } from '@/components/ui/phone-input'
import { customersService } from '@/services/customersService'
import { CustomerViewModel, CountryLookup, CityLookup } from '@/api/types'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'

/**
 * Customer Detail/Form Page
 * For creating and editing customers
 */
export const CustomersEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // Placeholder until company context is available

  const isEditMode = id !== 'new' && id !== undefined

  const [customer, setCustomer] = useState<CustomerViewModel>({
    customerNameEn: '',
    customerNameAr: '',
    email: '',
    phone: '',
    phoneNo: '',
    faxNo: '',
    mobileNo: '',
    website: '',
    customerType: 0,
    primaryPerson: '',
    isUoMApplicable: false,
    isUom: false,
    country: 0,
    countryAr: '',
    city: 0,
    cityAr: '',
    state: 0,
    stateAr: '',
    street: '',
    streetAr: '',
    citySubdivision: '',
    citySubdivisionAr: '',
    zipCode: '',
    plotNumber: '',
    plot: 0,
    buildingNumber: 0,
    address: '',
    addressAr: '',
    addressar: '',
    companyRegistrationNumber: '',
    companyRegistrationNo: '',
    crnNo: '',
    iqamaNo: '',
    vatNo: '',
    activeStatus: true,
    isActive: true,
    branchId: branchId || 0,
    companyId: companyId,
    chartOfAccountId: 0,
  })

  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])
  const [states, setStates] = useState<CityLookup[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  
  // Track last auto-filled addresses to avoid overwriting manual edits
  const lastAutoFilledAddressEn = useRef<string>('')
  const lastAutoFilledAddressAr = useRef<string>('')
  const isManualEdit = useRef<{ address: boolean; addressAr: boolean }>({ address: false, addressAr: false })

  // Load countries
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countriesData = await customersService.getCountries()
        setCountries(countriesData)
      } catch (error: any) {
        console.error('Failed to load countries', error)
        await showError('Error', t('customers.failedToLoadCountries'))
      }
    }
    loadCountries()
  }, [])

  // Load states
  useEffect(() => {
    const loadStates = async () => {
      try {
        const statesData = await customersService.getStates()
        setStates(statesData)
      } catch (error: any) {
        console.error('Failed to load states', error)
        await showError('Error', t('customers.failedToLoadStates'))
      }
    }
    loadStates()
  }, [])

  // Load cities when country changes
  useEffect(() => {
    if (customer.country && customer.country > 0) {
      const loadCities = async () => {
        try {
          const citiesData = await customersService.getCities(customer.country)
          setCities(citiesData)
        } catch (error: any) {
          console.error('Failed to load cities', error)
        }
      }
      loadCities()
    } else {
      setCities([])
    }
  }, [customer.country])

  // Helper function to build address string in the required format
  const buildAddressString = (
    buildingNumber: number | string,
    street: string,
    citySubdivision: string,
    zipCode: string,
    plotNumber: string | number,
    cityName: string,
    stateName: string,
    countryName: string,
    isArabic: boolean = false
  ): string => {
    const parts: string[] = []
    
    // Arabic labels
    const buildingLabel = isArabic ? 'رقم المبنى' : 'Building No.'
    const poLabel = isArabic ? 'ص.ب' : 'P.O No'
    const additionalLabel = isArabic ? 'الرقم الإضافي' : 'Additional No'
    
    if (buildingNumber && buildingNumber !== 0 && buildingNumber !== '') {
      parts.push(`${buildingLabel} ${buildingNumber}`)
    }
    if (street && street.trim()) {
      parts.push(street.trim())
    }
    if (citySubdivision && citySubdivision.trim()) {
      parts.push(citySubdivision.trim())
    }
    if (zipCode && zipCode.trim()) {
      parts.push(`${poLabel} ${zipCode.trim()}`)
    }
    if (plotNumber && plotNumber !== 0 && plotNumber !== '') {
      parts.push(`${additionalLabel} ${plotNumber}`)
    }
    if (cityName && cityName.trim()) {
      parts.push(cityName.trim())
    }
    if (stateName && stateName.trim()) {
      parts.push(stateName.trim())
    }
    if (countryName && countryName.trim()) {
      parts.push(countryName.trim())
    }
    
    return parts.join(', ')
  }

  // Auto-fill Address and AddressAr when address fields change
  useEffect(() => {
    // Get city name from lookup
    const selectedCity = cities.find(c => c.id === customer.city)
    const cityNameEn = selectedCity?.lookupNameEn || ''
    const cityNameAr = selectedCity?.lookupNameAr || customer.cityAr || ''
    
    // Get state name from lookup
    const selectedState = states.find(s => s.id === customer.state)
    const stateNameEn = selectedState?.lookupNameEn || ''
    const stateNameAr = selectedState?.lookupNameAr || customer.stateAr || ''
    
    // Get country name from lookup
    const selectedCountry = countries.find(c => c.id === customer.country)
    const countryNameEn = selectedCountry?.lookupNameEn || ''
    const countryNameAr = selectedCountry?.lookupNameAr || customer.countryAr || ''
    
    // Build English address
    const addressEn = buildAddressString(
      customer.buildingNumber || 0,
      customer.street || '',
      customer.citySubdivision || '',
      customer.zipCode || '',
      customer.plotNumber || customer.plot || 0,
      cityNameEn,
      stateNameEn,
      countryNameEn,
      false // English labels
    )
    
    // Build Arabic address with Arabic labels
    const addressAr = buildAddressString(
      customer.buildingNumber || 0,
      customer.streetAr || '',
      customer.citySubdivisionAr || '',
      customer.zipCode || '', // Zip code is usually numeric, same for both
      customer.plotNumber || customer.plot || 0,
      cityNameAr,
      stateNameAr,
      countryNameAr,
      true // Arabic labels
    )
    
    // Only update if address fields have values (at least one field is filled)
    const hasAddressData = customer.buildingNumber || 
      customer.street || 
      customer.streetAr || 
      customer.citySubdivision || 
      customer.citySubdivisionAr || 
      customer.zipCode || 
      customer.plotNumber || 
      customer.plot || 
      customer.city || 
      customer.state || 
      customer.country
    
    if (hasAddressData) {
      // Only auto-fill if:
      // 1. Address is empty, OR
      // 2. Address matches the last auto-filled value (user hasn't manually edited), OR
      // 3. It's the first time auto-filling
      const shouldUpdateEn = !customer.address || 
        customer.address === lastAutoFilledAddressEn.current || 
        lastAutoFilledAddressEn.current === ''
      
      const shouldUpdateAr = !customer.addressAr || 
        customer.addressAr === lastAutoFilledAddressAr.current || 
        lastAutoFilledAddressAr.current === ''
      
      if (shouldUpdateEn || shouldUpdateAr) {
        setCustomer((prev) => {
          const updated = { ...prev }
          
          if (shouldUpdateEn && addressEn) {
            updated.address = addressEn
            lastAutoFilledAddressEn.current = addressEn
            isManualEdit.current.address = false
          }
          
          if (shouldUpdateAr && addressAr) {
            updated.addressAr = addressAr
            updated.addressar = addressAr
            lastAutoFilledAddressAr.current = addressAr
            isManualEdit.current.addressAr = false
          }
          
          return updated
        })
      }
    }
  }, [
    customer.buildingNumber,
    customer.street,
    customer.streetAr,
    customer.citySubdivision,
    customer.citySubdivisionAr,
    customer.zipCode,
    customer.plotNumber,
    customer.plot,
    customer.city,
    customer.state,
    customer.country,
    cities,
    states,
    countries,
  ])

  // Load customer data if editing
  useEffect(() => {
    if (!isEditMode || !branchId || !id || countries.length === 0) return

    const loadCustomer = async () => {
      try {
        setLoading(true)
        const customerId = Number(id)
        const customerData = await customersService.getCustomerById(branchId, customerId)
        
        const countryId = customerData.country || 0
        const cityId = customerData.city || 0
        
        // Load cities if country is selected
        let citiesData: CityLookup[] = []
        if (countryId > 0) {
          try {
            citiesData = await customersService.getCities(countryId)
            setCities(citiesData)
          } catch (error) {
            console.error('Failed to load cities', error)
          }
        }
        
        // Find Arabic names from lookup data
        const selectedCountry = countries.find(c => c.id === countryId)
        const selectedCity = citiesData.find(c => c.id === cityId)
        const stateId = customerData.state || customerData.city || 0
        const selectedState = states.find(s => s.id === stateId)
        
        setCustomer({
          customerId: customerData.customerId || customerData.id,
          customerNameEn: customerData.customerNameEn || customerData.nameEn || customerData.name || '',
          customerNameAr: customerData.customerNameAr || customerData.nameAr || '',
          email: customerData.email || '',
          phone: customerData.phone || customerData.phoneNo || '',
          phoneNo: customerData.phoneNo || customerData.phone || '',
          mobileNo: customerData.mobileNo || customerData.mobile || '',
          website: customerData.website || '',
          customerType: customerData.customerType || 0,
          primaryPerson: customerData.primaryPerson || customerData.contactPerson || '',
          isUoMApplicable: customerData.isUom !== undefined ? customerData.isUom : (customerData.isUoMApplicable !== undefined ? customerData.isUoMApplicable : false),
          country: countryId,
          countryAr: customerData.countryAr || selectedCountry?.lookupNameAr || '',
          city: cityId,
          cityAr: customerData.cityAr || selectedCity?.lookupNameAr || '',
          street: customerData.street || '',
          streetAr: customerData.streetAr || '',
          citySubdivision: customerData.citySubdivision || '',
          citySubdivisionAr: customerData.citySubdivisionAr || '',
          zipCode: customerData.zipCode || customerData.postalCode || '',
          plotNumber: customerData.plotNumber || (customerData.plot ? String(customerData.plot) : '') || '',
          plot: customerData.plot || (customerData.plotNumber ? Number(customerData.plotNumber) : 0) || 0,
          buildingNumber: customerData.buildingNumber ? Number(customerData.buildingNumber) : 0,
          address: customerData.address || customerData.addressEn || '',
          addressAr: customerData.addressAr || customerData.addressar || '',
          addressar: customerData.addressar || customerData.addressAr || '',
          state: stateId,
          stateAr: customerData.stateAr || selectedState?.lookupNameAr || '',
          faxNo: customerData.faxNo || '',
          companyRegistrationNumber: customerData.companyRegistrationNumber || customerData.companyRegistrationNo || customerData.companyRegNumber || '',
          companyRegistrationNo: customerData.companyRegistrationNo || customerData.companyRegistrationNumber || customerData.companyRegNumber || '',
          iqamaNo: customerData.iqamaNo || customerData.iqamaNumber || '',
          vatNo: customerData.vatNo || customerData.vatNumber || '',
          activeStatus: customerData.activeStatus !== undefined ? customerData.activeStatus : (customerData.isActive !== undefined ? customerData.isActive : true),
          isActive: customerData.isActive !== undefined ? customerData.isActive : (customerData.activeStatus !== undefined ? customerData.activeStatus : true),
          isUom: customerData.isUom !== undefined ? customerData.isUom : (customerData.isUoMApplicable !== undefined ? customerData.isUoMApplicable : false),
          branchId: customerData.branchId || branchId,
          companyId: customerData.companyId || companyId,
          chartOfAccountId: customerData.chartOfAccountId || customerData.accountId || 0,
        })
        
        // Set last auto-filled addresses to current values to prevent overwriting on load
        lastAutoFilledAddressEn.current = customerData.address || customerData.addressEn || ''
        lastAutoFilledAddressAr.current = customerData.addressAr || customerData.addressar || ''
        isManualEdit.current = { address: false, addressAr: false }
      } catch (error: any) {
        console.error('Failed to load customer', error)
        await showError('Error', error.response?.data?.message || t('customers.failedToLoadCustomer'))
      } finally {
        setLoading(false)
      }
    }

    loadCustomer()
  }, [isEditMode, branchId, id, companyId, countries])

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('customers.pleaseSelectBranch'))
      return
    }

    if (!customer.customerNameEn && !customer.customerNameAr) {
      await showValidationError(t('customers.pleaseEnterCustomerName'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Customer...' : 'Saving Customer...',
        'Please wait while we process your request'
      )
      
      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Prepare company registration number - ensure it's a string (not null or undefined)
      const companyRegNo = customer.companyRegistrationNumber 
        ? String(customer.companyRegistrationNumber).trim() 
        : ''
      
      // Map to API structure
      // Prepare iqamaNo and vatNo based on country
      let iqamaNoValue: string | null = null
      let vatNoValue: string | null = null
      
      if (customer.country === 1) {
        // Country ID 1: Use VAT No, set iqamaNo to -1
        vatNoValue = customer.vatNo ? String(customer.vatNo).trim() : ''
        iqamaNoValue = '-1'
      } else {
        // Country ID not 1: Use IQAMA No, set vatNo to -1
        iqamaNoValue = customer.iqamaNo && customer.iqamaNo.trim() !== '' 
          ? String(customer.iqamaNo).trim() 
          : null
        vatNoValue = '-1'
      }
      
      const customerData: any = {
        customerId: customer.customerId || 0,
        customerNameEn: customer.customerNameEn || '',
        customerNameAr: customer.customerNameAr || '',
        customerType: customer.customerType || 0, // Transaction type - required by API
        primaryPerson: customer.primaryPerson || '',
        email: customer.email || '',
        phoneNo: customer.phoneNo || customer.phone || '',
        faxNo: customer.faxNo || '',
        mobileNo: customer.mobileNo || '',
        website: customer.website || '',
        address: customer.address || '',
        addressar: customer.addressar || customer.addressAr || '',
        street: customer.street || '',
        streetAr: customer.streetAr || '',
        buildingNumber: customer.buildingNumber ? Number(customer.buildingNumber) : 0,
        plot: customer.plot || (customer.plotNumber ? Number(customer.plotNumber) : 0) || 0,
        citySubdivisionAr: customer.citySubdivisionAr || '',
        citySubdivision: customer.citySubdivision || '',
        zipCode: customer.zipCode || '',
        city: customer.city || 0, // City ID from city dropdown
        country: customer.country || 0,
        state: customer.state || 0, // State ID from state dropdown
        companyRegistrationNo: companyRegNo || '',
        vatNo: vatNoValue,
        iqamaNo: iqamaNoValue,
        isUom: customer.isUom !== undefined ? customer.isUom : (customer.isUoMApplicable || false),
        isActive: customer.isActive !== undefined ? customer.isActive : (customer.activeStatus !== undefined ? customer.activeStatus : true),
        branchId: branchId || 0,
        ipAddress: ipAddress, // Set logged-in user's IP address
      }

      // Debug logging to check what's being sent
      console.log('=== Customer Save Payload ===')
      console.log('Mode:', isEditMode ? 'UPDATE' : 'CREATE')
      console.log('Payload:', customerData)
      console.log('Payload JSON:', JSON.stringify(customerData, null, 2))
      console.log('Branch ID:', branchId)
      console.log('IP Address:', ipAddress)
      console.log('Customer Type:', customerData.customerType)
      console.log('============================')
     
      if (isEditMode) {
        await customersService.updateCustomer(customerData)
      } else {
        await customersService.saveCustomer(customerData)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('customers.customerUpdatedSuccess') : t('customers.customerCreatedSuccess')
      )

      navigate('/customers')
    } catch (error: any) {
      console.error('Failed to save customer', error)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const countryOptions: SearchableSelectOption[] = countries.map((country) => ({
    value: String(country.id || 0),
    label: language === 'ar' 
      ? (country.lookupNameAr || country.lookupNameEn || '') 
      : (country.lookupNameEn || country.lookupNameAr || ''),
  }))

  const cityOptions: SearchableSelectOption[] = cities.map((city) => ({
    value: String(city.id || 0),
    label: language === 'ar' 
      ? (city.lookupNameAr || city.lookupNameEn || '') 
      : (city.lookupNameEn || city.lookupNameAr || ''),
  }))

  const stateOptions: SearchableSelectOption[] = states.map((state) => ({
    value: String(state.id || 0),
    label: language === 'ar' 
      ? (state.lookupNameAr || state.lookupNameEn || '') 
      : (state.lookupNameEn || state.lookupNameAr || ''),
  }))

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditMode ? t('customers.editCustomer') : t('customers.addNewCustomerTitle')}
        description={isEditMode ? t('customers.updateCustomerInformation') : t('customers.createNewCustomer')}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <div className="space-y-6">
          {/* Customer Info Section */}
          <Card>
          <CardHeader>
            <CardTitle>{t('customers.customerInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerNameEn">{t('customers.name')}</Label>
                <Input
                  id="customerNameEn"
                  value={customer.customerNameEn || ''}
                  onChange={(e) => setCustomer({ ...customer, customerNameEn: e.target.value })}
                  placeholder={t('customers.enterCustomerName')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerNameAr">{t('customers.nameArabic')}</Label>
                <Input
                  id="customerNameAr"
                  value={customer.customerNameAr || ''}
                  onChange={(e) => setCustomer({ ...customer, customerNameAr: e.target.value })}
                  placeholder={t('customers.enterCustomerNameArabic')}
                />
              </div>
              <div className="space-y-2 sm:col-span-2 md:col-span-1">
                <Label htmlFor="primaryPerson">{t('customers.primaryPerson')}</Label>
                <Input
                  id="primaryPerson"
                  value={customer.primaryPerson || ''}
                  onChange={(e) => setCustomer({ ...customer, primaryPerson: e.target.value })}
                  placeholder={t('customers.enterPrimaryContactPerson')}
                />
              </div>
              <div className="space-y-2 sm:col-span-2 md:col-span-1">
                <Label htmlFor="isUoMApplicable">{t('customers.isUoMApplicable')}</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isUoMApplicable"
                    checked={customer.isUom !== undefined ? customer.isUom : (customer.isUoMApplicable || false)}
                    onCheckedChange={(checked) => setCustomer({ ...customer, isUoMApplicable: checked === true, isUom: checked === true })}
                  />
                  <Label htmlFor="isUoMApplicable" className="font-normal cursor-pointer">
                    {t('customers.yes')}
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

          {/* Contact Details Section */}
          <Card>
          <CardHeader>
            <CardTitle>{t('customers.contactDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{t('customers.phoneNo')}</Label>
                <Input
                  id="phone"
                  value={customer.phone || customer.phoneNo || ''}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value, phoneNo: e.target.value })}
                  placeholder={t('customers.enterPhoneNumber')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="faxNo">{t('customers.faxNo')}</Label>
                <Input
                  id="faxNo"
                  value={customer.faxNo || ''}
                  onChange={(e) => setCustomer({ ...customer, faxNo: e.target.value })}
                  placeholder={t('customers.enterFaxNumber')}
                />
              </div>
              <div className="space-y-2 sm:col-span-2 md:col-span-1">
                <PhoneInput
                  key={`mobile-${customer.customerId || 'new'}`}
                  id="mobileNo"
                  label={t('customers.mobileNo')}
                  value={customer.mobileNo || ''}
                  onChange={(value) => setCustomer({ ...customer, mobileNo: value })}
                  placeholder={t('customers.enterMobileNumber')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">{t('customers.website')}</Label>
                <Input
                  id="website"
                  value={customer.website || ''}
                  onChange={(e) => setCustomer({ ...customer, website: e.target.value })}
                  placeholder={t('customers.enterWebsiteUrl')}
                />
              </div>
              <div className="space-y-2 sm:col-span-2 md:col-span-1">
                <Label htmlFor="email">{t('customers.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={customer.email || ''}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  placeholder={t('customers.enterEmailAddress')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

          {/* Address Details Section */}
          <Card>
          <CardHeader>
            <CardTitle>{t('customers.addressDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Left Side - English Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="country">{t('customers.country')}</Label>
                  <SearchableSelect
                    options={countryOptions}
                    value={customer.country ? String(customer.country) : ''}
                    onValueChange={(value) => {
                      const selectedCountry = countries.find(c => c.id === Number(value))
                      setCustomer({ 
                        ...customer, 
                        country: Number(value) || 0, 
                        countryAr: selectedCountry?.lookupNameAr || '',
                        city: 0,
                        cityAr: '',
                        state: 0,
                        stateAr: ''
                      })
                    }}
                    placeholder={t('customers.selectCountry')}
                    searchPlaceholder={t('customers.searchCountries')}
                    emptyMessage={t('customers.noCountriesFound')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">{t('customers.city')}</Label>
                  <SearchableSelect
                    options={cityOptions}
                    value={customer.city ? String(customer.city) : ''}
                    onValueChange={(value) => {
                      const selectedCity = cities.find(c => c.id === Number(value))
                      setCustomer({ 
                        ...customer, 
                        city: Number(value) || 0,
                        cityAr: selectedCity?.lookupNameAr || ''
                      })
                    }}
                    placeholder={t('customers.selectCity')}
                    searchPlaceholder={t('customers.searchCities')}
                    emptyMessage={t('customers.noCitiesFound')}
                    disabled={!customer.country || customer.country === 0}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">{t('customers.state')}</Label>
                  <SearchableSelect
                    options={stateOptions}
                    value={customer.state ? String(customer.state) : ''}
                    onValueChange={(value) => {
                      const selectedState = states.find(s => s.id === Number(value))
                      setCustomer({ 
                        ...customer, 
                        state: Number(value) || 0,
                        stateAr: selectedState?.lookupNameAr || ''
                      })
                    }}
                    placeholder={t('customers.selectState')}
                    searchPlaceholder={t('customers.searchStates')}
                    emptyMessage={t('customers.noStatesFound')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street">{t('customers.street')}</Label>
                  <Input
                    id="street"
                    value={customer.street || ''}
                    onChange={(e) => setCustomer({ ...customer, street: e.target.value })}
                    placeholder={t('customers.enterStreet')}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="citySubdivision">{t('customers.citySubdivision')}</Label>
                  <Input
                    id="citySubdivision"
                    value={customer.citySubdivision || ''}
                    onChange={(e) => setCustomer({ ...customer, citySubdivision: e.target.value })}
                    placeholder={t('customers.enterCitySubdivision')}
                    className="w-full"
                  />
                </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipCode">{t('customers.zipCode')}</Label>
                    <Input
                      id="zipCode"
                      value={customer.zipCode || ''}
                      onChange={(e) => setCustomer({ ...customer, zipCode: e.target.value })}
                      placeholder={t('customers.enterZipCode')}
                    className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="plotNumber">{t('customers.plotNumber')}</Label>
                    <Input
                      id="plotNumber"
                    type="number"
                    value={customer.plotNumber || customer.plot || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      const numValue = value === '' ? 0 : Number(value)
                      setCustomer({ ...customer, plotNumber: value, plot: numValue })
                    }}
                      placeholder={t('customers.enterPlotNumber')}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Right Side - Arabic Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="countryAr">{t('customers.countryAr')}</Label>
                  <Input
                    id="countryAr"
                    value={customer.countryAr || ''}
                    readOnly
                    placeholder={t('customers.enterCountryNameArabic')}
                    className="w-full bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cityAr">{t('customers.cityAr')}</Label>
                  <Input
                    id="cityAr"
                    value={customer.cityAr || ''}
                    readOnly
                    placeholder={t('customers.enterCityNameArabic')}
                    className="w-full bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stateAr">{t('customers.stateAr')}</Label>
                  <Input
                    id="stateAr"
                    value={customer.stateAr || ''}
                    readOnly
                    placeholder={t('customers.enterStateArabic')}
                    className="w-full bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="streetAr">{t('customers.streetAr')}</Label>
                  <Input
                    id="streetAr"
                    value={customer.streetAr || ''}
                    onChange={(e) => setCustomer({ ...customer, streetAr: e.target.value })}
                    placeholder={t('customers.enterStreetArabic')}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="citySubdivisionAr">{t('customers.citySubdivisionAr')}</Label>
                  <Input
                    id="citySubdivisionAr"
                    value={customer.citySubdivisionAr || ''}
                    onChange={(e) => setCustomer({ ...customer, citySubdivisionAr: e.target.value })}
                    placeholder={t('customers.enterCitySubdivisionArabic')}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buildingNumber">{t('customers.buildingNumber')}</Label>
                  <Input
                    id="buildingNumber"
                    type="number"
                    value={customer.buildingNumber || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      const numValue = value === '' ? 0 : Number(value)
                      setCustomer({ ...customer, buildingNumber: numValue })
                    }}
                    placeholder={t('customers.enterBuildingNumber')}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="activeStatus">{t('customers.activeStatus')}</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="activeStatus"
                      checked={customer.isActive !== undefined ? customer.isActive : (customer.activeStatus !== undefined ? customer.activeStatus : true)}
                      onCheckedChange={(checked) => setCustomer({ ...customer, activeStatus: checked === true, isActive: checked === true })}
                    />
                    <Label htmlFor="activeStatus" className="font-normal cursor-pointer">
                      {t('common.active')}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Address Fields - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="address">{t('customers.address')}</Label>
                <Textarea
                  id="address"
                  value={customer.address || ''}
                  onChange={(e) => {
                    isManualEdit.current.address = true
                    lastAutoFilledAddressEn.current = e.target.value
                    setCustomer({ ...customer, address: e.target.value })
                  }}
                  placeholder={t('customers.enterFullAddress')}
                  rows={3}
                  className="w-full"
                />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="addressAr">{t('customers.addressAr')}</Label>
                  <Textarea
                    id="addressAr"
                  value={customer.addressAr || customer.addressar || ''}
                  onChange={(e) => {
                    isManualEdit.current.addressAr = true
                    lastAutoFilledAddressAr.current = e.target.value
                    setCustomer({ ...customer, addressAr: e.target.value, addressar: e.target.value })
                  }}
                    placeholder={t('customers.enterFullAddressArabic')}
                    rows={3}
                  className="w-full"
                  />
              </div>
            </div>
          </CardContent>
        </Card>

          {/* Tax Details Section */}
          <Card>
          <CardHeader>
            <CardTitle>{t('customers.taxDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Left Side - Company Registration No */}
              <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyRegistrationNumber">{t('customers.companyRegistrationNo')}</Label>
                <Input
                  id="companyRegistrationNumber"
                  type="number"
                  value={customer.companyRegistrationNumber || ''}
                  onChange={(e) => {
                    const value = e.target.value
                    // Only allow numbers - allow typing any length, validation will show error if less than 5
                    if (value === '' || /^\d+$/.test(value)) {
                      setCustomer({ ...customer, companyRegistrationNumber: value })
                    }
                  }}
                  placeholder={t('customers.enterRegistrationNumber')}
                  minLength={5}
                    className="w-full"
                />
                {customer.companyRegistrationNumber && customer.companyRegistrationNumber.length > 0 && customer.companyRegistrationNumber.length < 5 && (
                  <p className="text-sm text-destructive mt-1">{t('customers.registrationNumberMinDigits')}</p>
                )}
              </div>
              </div>

              {/* Right Side - VAT No (for country ID 1) or IQAMA No (for other countries) */}
              <div className="space-y-4">
              {customer.country === 1 ? (
                <div className="space-y-2">
                  <Label htmlFor="vatNo">{t('customers.vatNo')}</Label>
                  <Input
                    id="vatNo"
                    type="text"
                    value={customer.vatNo || ''}
                    onChange={(e) => {
                      setCustomer({ ...customer, vatNo: e.target.value })
                    }}
                    placeholder={t('customers.enterVatNo')}
                      className="w-full"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="iqamaNo">{t('customers.iqamaNo')}</Label>
                  <Input
                    id="iqamaNo"
                    type="number"
                    value={customer.iqamaNo || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      // Only allow numbers and enforce maximum 14 digits
                      if (value === '' || (value.length <= 14 && /^\d+$/.test(value))) {
                        setCustomer({ ...customer, iqamaNo: value })
                      }
                    }}
                    placeholder={t('customers.enterIqamaNo')}
                    maxLength={14}
                      className="w-full"
                  />
                  {customer.iqamaNo && customer.iqamaNo.length > 14 && (
                    <p className="text-sm text-destructive mt-1">{t('customers.iqamaNumberMaxDigits')}</p>
                  )}
                </div>
              )}
              </div>
            </div>
          </CardContent>
        </Card>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/customers')} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('customers.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? t('customers.updateCustomer') : t('customers.saveCustomer')}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
